import { Injectable, inject } from '@angular/core';
import {
  EnvironmentService,
  LocalStorageService,
  NotificationService
} from '@shared/services';
import {
  Subject,
  Observable,
  BehaviorSubject,
  fromEvent,
  EMPTY,
  timer
} from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { ApplicationFacade } from '../facades/application.facade';

export type WebSocketStatus =
  | 'CONNECTING'
  | 'CONNECTED'
  | 'DISCONNECTED'
  | 'ERROR';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private readonly appFacade = inject(ApplicationFacade);
  private readonly envService = inject(EnvironmentService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);

  private readonly destroySubject = new Subject<void>();
  private readonly messagesSubject = new Subject<unknown>();
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectInterval = 5 * 1000;
  private readonly secondaryReconnectInterval = 60 * 1000;

  private readonly connectionStatusSubject =
    new BehaviorSubject<WebSocketStatus>('DISCONNECTED');

  private socketSubject: WebSocketSubject<unknown> | null = null;
  private reconnectAttempts = 0;
  private notificationId: string = undefined;
  private reconnectionTimeout: ReturnType<typeof setTimeout> | undefined =
    undefined;

  readonly messages$ = this.messagesSubject.asObservable();
  readonly connectionStatus$ = this.connectionStatusSubject.asObservable();

  /**
   * Connect to the WebSocket server
   * @param url WebSocket URL, defaults to environment config
   */
  connect(url: string): void {
    const socketUrl = `${this.envService.webSocketHost}/${url}`;

    if (this.socketSubject) {
      return;
    }

    this.connectionStatusSubject.next('CONNECTING');

    this.socketSubject = webSocket({
      url: socketUrl,
      protocol: this.addTokenToProtocol('Authorization', 'impersonator'),
      openObserver: {
        next: () => {
          if (this.notificationId) {
            this.handleUserNotifications('CONNECTED');
          }

          this.connectionStatusSubject.next('CONNECTED');
          this.reconnectAttempts = 0;
        }
      },
      closeObserver: {
        next: () => {
          this.connectionStatusSubject.next('DISCONNECTED');
          this.socketSubject = null;
          this.reconnect(url);
          this.handleUserNotifications('DISCONNECTED');
        }
      }
    });

    this.socketSubject
      .pipe(
        takeUntil(this.destroySubject),
        catchError(err => {
          this.connectionStatusSubject.next('ERROR');
          this.socketSubject = null;
          this.reconnect(url);
          this.handleUserNotifications('ERROR');

          console.error('WebSocket error:', err);

          return EMPTY;
        })
      )
      .subscribe({
        next: message => {
          this.messagesSubject.next(message);
        }
      });

    this.sendHeartbeat();
    this.setupOnlineStatusListeners();
  }

  /**
   * Send a message to the WebSocket server
   * @param data Message to send
   * @returns Boolean indicating if message was sent
   */
  send<T>(data: T): boolean {
    if (!this.socketSubject) {
      return false;
    }

    try {
      this.socketSubject.next(data);
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Close the WebSocket connection
   */
  closeConnection(): void {
    this.destroySubject.next();

    if (this.socketSubject) {
      this.socketSubject.complete();
      this.socketSubject = null;
    }

    this.connectionStatusSubject.next('DISCONNECTED');
  }

  /**
   * Get a typed stream of messages from the WebSocket
   *
   * @returns Observable of typed messages
   */
  getMessages<T>(): Observable<T> {
    return this.messages$.pipe(map(message => message as T));
  }

  /**
   * Attempts to reconnect to the WebSocket server with exponential backoff.
   *
   * @param url - The WebSocket URL to reconnect to
   */
  private reconnect(url: string): void {
    if (!this.appFacade.isLoggedIn()) {
      return;
    }

    if (this.reconnectionTimeout) {
      clearTimeout(this.reconnectionTimeout);
    }

    let reconnectInterval = this.reconnectInterval;

    // Check for maximum 5s reconnection attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      reconnectInterval = this.secondaryReconnectInterval;
    }

    this.reconnectionTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(url);

      console.warn(`Attempting to reconnect (#${this.reconnectAttempts})...`);
    }, reconnectInterval);
  }

  /**
   * Setup browser online/offline event listeners
   */
  private setupOnlineStatusListeners(): void {
    fromEvent(window, 'online')
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        console.warn('Browser is online, attempting to reconnect WebSocket.');

        if (this.connectionStatusSubject.value !== 'CONNECTED') {
          this.connect('updates');
        }
      });

    fromEvent(window, 'offline')
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        console.warn('Browser is offline, WebSocket will be disconnected.');

        if (this.socketSubject) {
          this.socketSubject.complete();
          this.socketSubject = null;
        }

        this.connectionStatusSubject.next('DISCONNECTED');
      });
  }

  /**
   * TODO: test to see how it works with the new cookie token!
   *
   * Get's the token from local storage and encodes it for use in WebSocket protocol
   *
   * @param headerNames Array of header names to retrieve from local storage
   * @returns Array of encoded header names and their corresponding tokens
   */
  private addTokenToProtocol(...headerNames: string[]): string[] {
    const result: string[] = [];

    headerNames.forEach(headerName => {
      const value = this.localStorageService.get<string>(headerName);

      if (value) {
        const token = value.replace('Bearer ', '');
        result.push(encodeURIComponent(headerName), encodeURIComponent(token));
      }
    });

    return result;
  }

  private handleUserNotifications(status: WebSocketStatus): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      return;
    }

    if (this.notificationId) {
      this.notificationService.close(this.notificationId);
      this.notificationId = undefined;
    }

    if (status === 'DISCONNECTED' || status === 'ERROR') {
      this.notificationId = this.notificationService.warning(
        'notifications.websockets.disconnected',
        0
      ).messageId;

      return;
    }

    this.notificationId = this.notificationService.info(
      'notifications.websockets.reconnected',
      3000
    ).messageId;
  }

  /**
   * Sends a heartbeat ping to the WebSocket server every 50 seconds
   * to keep the connection alive.
   */
  private sendHeartbeat(): void {
    timer(50 * 1000, 50 * 1000)
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        if (!this.appFacade.isLoggedIn()) {
          return;
        }

        if (this.connectionStatusSubject.value !== 'CONNECTED') {
          return;
        }

        this.send({
          screen: 'ping'
        });
      });
  }
}
